import React, { useContext, useEffect, useMemo, useState } from "react";
import CheckBoxToggle from "../../../components/shared/CheckBoxToggle";
import { Accordion, Button, Icon, Modal } from "semantic-ui-react";
import { useMessageResources, useTextResources } from "../../../language";
import PropTypes from "prop-types";
import GridPanel from "../../../components/GridPanel";
import { dateFormat, Designations } from "../../../components/shared/constants";
import moment from "moment";
import { PriceListContext } from "../context";
import { PriceLists } from "../../../api/services/next";
import { onError, onWarning, onSuccess } from "../../../components/shared/messages";
import NumericInput from "../../../components/shared/NumericInput";
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { useConfirm } from "../../../hooks/useConfirm";
import Weekdays from "../../../components/shared/Weekdays";
import { useLoader } from "../../../hooks/useLoader";

export const CombinationsPopup = ({ open, onClose, EntityId, EntityTypeId, roomType, pricingTemplates, updateRPEs, rateElementName }) => {
  const loader = useLoader();
  const currentHotel = useCurrentHotel();
  const confirm  = useConfirm();
  const text = useTextResources();
  const message = useMessageResources();
  const [openCombinationRanges, setOpenCombinationRanges] = useState([0]);
  const [pricingElements, setPricingElements] = useState([]);
  const { state } = useContext(PriceListContext);
  const [isDirty, setIsDirty] = useState(false);

  const priceToUpdate = useMemo(() => {
    return state?.IsProductPricingIncludesTax ? "AmountAfterTax" : "AmountBeforeTax";
  }, [state?.IsProductPricingIncludesTax]);

  const getCombinationsPossibilitiesOfRoom = (pricingElementIndex, room, rpe, RatePricingElementId) => {
    const doublePrice = rpe.RatePerPeople?.find(x => !x.ExceptionDate)?.RatePerPersonPrices?.find(x => x.DesignationId === Designations.Double)?.[priceToUpdate];
    const fittingTemplate = [...pricingTemplates]?.find(x => x.Id === rpe.PricingTemplateId);
    let combinations = [];

    for (let adults = 0; adults <= room?.MaxAdults; adults++) {
      for (let children = 0; children <= room?.MaxChildren; children++) {

        const fittingPricingTemplatePrice = fittingTemplate?.PricingTemplatesPrices?.find(x => x.Adults === adults && x.Children === children);
        const templatePriceBaseChildren = fittingTemplate?.PricingTemplatesPrices?.find(p => p.BasePriceForChildrenInSeparateRoomCalculation);
        let baseDoublePrice = doublePrice;
        
        if (adults + children <= room.MaxPax && adults + children > 0) {
          let existingCombination = rpe.RatePerCombinations?.find(x => x.Adult === adults && x.Child === children);
          if (adults == 0 && children > 0 && templatePriceBaseChildren && templatePriceBaseChildren?.Id != fittingPricingTemplatePrice?.Id) {
            baseDoublePrice = doublePrice * (templatePriceBaseChildren.PriceAdjustment / 100);
          }

          if (existingCombination && !fittingPricingTemplatePrice) {
            combinations.push(existingCombination);
          } else
            combinations.push({
              Adult: adults,
              Child: children,
              RatePricingElementId,
              RoomTypeId: EntityId,
              RateId: rpe.RateId ?? state.Id,
              Id: existingCombination?.Id,
              [priceToUpdate]: fittingPricingTemplatePrice ? (fittingPricingTemplatePrice.PriceAdjustment / 100) * baseDoublePrice : undefined,
              calculatedFromTemplate: Boolean(fittingPricingTemplatePrice),
              IsActive: existingCombination?.IsActive ?? true,
            });
        }
      }
    }
    return combinations.map((c, combinationIndex) => ({ ...c, combinationIndex, pricingElementIndex }));
  }

  const calculateCombinationBasePrice = (combination, prices, priceToUpdate) => {
    let adults = combination.Adult || 0;
    let children = combination.Child || 0;

    if (adults + children === 1)
      return prices.find(x => x.DesignationId === Designations.Single)?.[priceToUpdate] || 0;

    let sum = prices.find(x => x.DesignationId === Designations.Double)?.[priceToUpdate] || 0;
    adults -= 2;
    if (adults < 0) {
      children += adults;
      adults = 0;
    }

    let maxExtraAdult = Math.max.apply(null, prices.filter(x => x.DesignationId <= Designations.FourthExtraAdult).map(x => x.DesignationId)) - Designations.FirstExtraAdult;
    for (let i = 0; i < adults; i++) {
      let nth = Math.max(0, Math.min(maxExtraAdult, i));
      let p = prices.find(x => x.DesignationId === Designations.FirstExtraAdult + nth);
      if (p)
        sum += p[priceToUpdate];
    }

    let maxExtraChild = Math.max.apply(null, prices.filter(x => x.DesignationId <= Designations.FourthExtraChild).map(x => x.DesignationId)) - Designations.FirstExtraChild;
    for (let i = 0; i < children; i++) {
      let nth = Math.max(0, Math.min(maxExtraChild, i));
      let p = prices.find(x => x.DesignationId === Designations.FirstExtraChild + nth);
      if (p)
        sum += p[priceToUpdate];
    }
    return sum;
  }

  useEffect(() => {
    if (!open) {
      setOpenCombinationRanges([]);
      setPricingElements([]);
      setIsDirty(false);
      return;
    }
    const ratePricingElements = state.RatePricingElements?.filter(rpe => rpe.EntityTypeId === EntityTypeId && rpe.EntityId === EntityId && rpe.IsActive);
    if (roomType) {
      const rateElementsWithPrices = ratePricingElements.map((rpe, pricingElementIndex) => {
        const fittingRatePerPerson = rpe.RatePerPeople?.find(x => !x.ExceptionDate) || {
          RatePricingElementId: 0,
          RatePerPersonPrices: [],
          RateId: state.Id,
          RoomTypeId: EntityId,
        };
        const combinations = getCombinationsPossibilitiesOfRoom(pricingElementIndex, roomType, rpe, rpe.Id).map(x => ({ ...x, BasePrice: calculateCombinationBasePrice(x, fittingRatePerPerson.RatePerPersonPrices, priceToUpdate) }));
        return {
          ...rpe,
          RatePerPersonId: fittingRatePerPerson.Id,
          RatePerCombinations: combinations
        };
      });
      setPricingElements(rateElementsWithPrices.sort((a, b) => a.StartDate < b.StartDate ? -1 : 1));
    }
  }, [state?.RatePricingElements, EntityId, EntityTypeId, open])

  const gridOptions = {
    columns: [
      {
        name: "IsActive",
        title: text("Active"),
        formatter: ({ value, row, column }) => <CheckBoxToggle
          checked={value}
          onChange={(value) => onUpdate(value, row, column)}
          disabled={row.RateId === state.BaseRateId}
        />,
        editor: <CheckBoxToggle />
      },
      {
        name: "Adult",
        title: text("Adults"),
      },
      {
        name: "Child",
        title: text("Children"),
      },
      {
        name: "BasePrice",
        title: `${text("BasePrice")}`,
        formatter: ({ value }) => value.toFixed(2)
      },
      {
        name: priceToUpdate,
        title: `${text("OverridePrice")}`,
        formatter: ({ value, row, column }) => <NumericInput selectOnFocus disabled={row.calculatedFromTemplate || row.RateId === state.BaseRateId} buttonPlacement="none" value={value} digits={2} min={0} onChangeComplete={value => onUpdate(value, row, column)} />,
      },
    ],
    noVirtualTable: true
  };


  const onSave = async () => {
    try {
      loader(true);
      const pricingElementsOnlyUpdatedCombinations = pricingElements.map(rpe => ({
        ...rpe,
        RatePerCombinations: rpe.RatePerCombinations.filter(x => x.isUpdated).map(x => ({
          ...x,
          AmountBeforeTax: x.AmountBeforeTax || 0,
          AmountAfterTax: x.AmountAfterTax || 0
        }))
      }));
      await PriceLists.UpdatePricingElements({
        StartDate: state.StartDate,
        EndDate: state.EndDate,
        RatePricingElements: pricingElementsOnlyUpdatedCombinations,
        HotelId: state.HotelId,
        CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
        IsProductPricingIncludesTax: state?.IsProductPricingIncludesTax,
        IsDistributeToChannelManager: state.IsDistributeToChannelManager,
        RateCategoryId: state.RateCategoryId,
        Id: state.Id,
        BaseRateId: state.BaseRateId
      });
      updateRPEs();
      onSuccess();
      onClose();
    } catch (error) {
      error.status === 400 ? onWarning(error) : onError(error);
    } finally {
      loader(false);
    }
  };

  const onUpdate = (value, row, column) => {
    if (!value || value === 0) {
      row[column.name] = undefined;
    }
    setIsDirty(true);
    if(row.RatePricingElementId){
        let updatedRpe = pricingElements.find(rpe => rpe.Id === row.RatePricingElementId);
        if (updatedRpe) updatedRpe.IsUpdated = true;
    }
    row[column.name] = value;
    row.isUpdated = true;
    setPricingElements([...pricingElements]);
  }

  const handleClick = (index) => {
    if (openCombinationRanges.includes(index))
      setOpenCombinationRanges(openCombinationRanges.filter((x) => x !== index));
    else setOpenCombinationRanges([...openCombinationRanges, index]);
  };

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm(
        {
          content: `${message("WouldYouLikeToContinue")}?`,
          confirmButton: {
            content: text('Yes'),
            positive: true,
          },
          cancelButton: {
            content: text('No'),
            negative: true,
          },
        }))
        onClose();
    }
    else onClose();
  }

  return (
    <Modal size="large" style={{ maxWidth: "none", width: "70vw" }} open={open} dimmer centered className="price-list-settings">
      <Modal.Header>{`${text("EditCombinations")} - ${rateElementName}`}</Modal.Header>
      <Modal.Content scrolling className="combinations-popup">
        <Accordion styled fluid>
          {pricingElements.map((combinationRange, index) => (
            <>
              <Accordion.Title
                key={index}
                active={openCombinationRanges.includes(index)}
                onClick={() => handleClick(index)}
              >
                <Icon name="dropdown" />
                {moment(combinationRange.StartDate).format(dateFormat)} -{" "}
                {moment(combinationRange.EndDate).format(dateFormat)}
                {" "}<Weekdays selectedWeekdaysInt={combinationRange.Weekdays} />
              </Accordion.Title>
              <Accordion.Content active={openCombinationRanges.includes(index)} className="grid-panel">
                <GridPanel data={combinationRange.RatePerCombinations} options={gridOptions} />
              </Accordion.Content>
            </>
          ))}
        </Accordion>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose}>
          {text("Close")}
        </Button>
        <Button positive onClick={onSave}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

CombinationsPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateRPEs: PropTypes.func,
  EntityId: PropTypes.number,
  EntityTypeId: PropTypes.number,
  roomType: PropTypes.object,
  pricingTemplates: PropTypes.array,
  rateElementName: PropTypes.string,
}
